import { observer } from 'mobx-react';
import { store } from 'Models/Store';
import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { previousCardsQueryOptions } from 'Services/Api/_HumanWritten/PaymentService';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { LottieWaveDotLoader } from 'Views/Components/_HumanWritten/Lottie/LottieWaveDotLoader';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import If from 'Views/Components/If/If';
import { PaymentMethodId } from './PaymentTypeWrapper';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { SandboxBadge, SandboxTooltipBadge } from './SandboxPaymentHelpers';
import { useQuery } from '@tanstack/react-query';
import Spinner from '../../Spinner/Spinner';

export interface PaymentTypeSelectionProps {
	disabled: boolean;
	canInvoice: boolean;
	/**
	 * When true the invoice option is shown but disabled, with an explanation that a booking
	 * which has previously been paid for can't be invoiced. Mutually exclusive with `canInvoice`.
	 */
	invoiceDisabledForPaidBooking?: boolean;
	/**
	 * Render the "pay later" group (invoice / pay later) above the "pay now" group. Used for
	 * invoiced customers, who default to Send invoice, so their primary method leads.
	 */
	payLaterSectionFirst?: boolean;
	canTakeCash: boolean;
	canPayLater: boolean;
	canPayByTerminal: boolean;
	/** Whether the credit-card option is offered. Defaults to true for non-wizard flows. */
	canPayByCard?: boolean;
	showEftpos: boolean;
	canPayWithPreviousCards: boolean;
	activePaymentMethod?: PaymentMethodId;
	togglePayment: (paymentMethodId?: PaymentMethodId) => void;
	cardPaymentMethod?: JSX.Element;
	userId?: string;
	/** Is the payment for a guest booking?  */
	isGuest?: boolean;
}

/**
 * How long the booking wizard reuses its prefetched saved-card list. Covers the wait between PaymentTab
 * mounting (where the list is fetched afresh) and this list rendering: extend-reservation, the
 * refund-method modal on an alteration, and setup-payment.
 */
const PREFETCHED_PREVIOUS_CARDS_STALE_MS = 60_000;

interface PaymentOption {
	id: PaymentMethodId;
	condition: boolean;
	/** Render the option but block selection (greyed out). Used for explanatory disabled states. */
	disabled?: boolean;
	label: JSX.Element;
	description: JSX.Element;
	/**
	 * Always-visible note shown under the label in the header. Use for disabled options, whose
	 * collapsible content/description can never be expanded (the option can't be clicked).
	 */
	headerNote?: JSX.Element;
	additionalContent?: JSX.Element;
	/**
	 * Optional stable test id used for e2e flows. Provided for options whose
	 * `id` is dynamic (e.g. previous cards use the Stripe PaymentMethod id).
	 */
	dataTestId?: string;
}

function PaymentTypeSelection({
	disabled,
	canInvoice,
	invoiceDisabledForPaidBooking,
	payLaterSectionFirst,
	canTakeCash,
	canPayLater,
	canPayByTerminal,
	canPayByCard = true,
	showEftpos,
	canPayWithPreviousCards,
	activePaymentMethod,
	togglePayment,
	cardPaymentMethod,
	userId,
	isGuest,
}: PaymentTypeSelectionProps) {
	const intl = useIntl();
	const hasAnyPaymentOptions =
		canPayByCard ||
		canInvoice ||
		canTakeCash ||
		canPayLater ||
		canPayByTerminal ||
		showEftpos ||
		!!invoiceDisabledForPaidBooking;

	const { paymentProcessing, paymentMethod, clearTerminalPolling, paymentLocation } = usePaymentCheckOut();

	// Create base options as a function to avoid recreation issues
	const payNowOptions = [
		{
			id: 'eftpos-option',
			condition: showEftpos,
			label: <FormattedMessage id="eftpos__name__label" defaultMessage="Eftpos" />,
			description: (
				<FormattedMessage
					id="eftpos-payment-option__description"
					defaultMessage="Please confirm you have entered the correct price when taking payment."
				/>
			),
		},
		{
			id: 'terminal-option',
			condition: canPayByTerminal,
			label: (
				<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<FormattedMessage id="pos__payment-option__label" defaultMessage="Integrated POS" />
					<SandboxTooltipBadge tooltip="Requires a Stripe simulated terminal reader configured in the test mode dashboard." />
				</span>
			),
			description: (
				<FormattedMessage
					id="pos__payment-option__description"
					defaultMessage="Send to an integrated POS device to complete the booking."
				/>
			),
		},
		{
			id: 'cash-option',
			condition: canTakeCash,
			label: <FormattedMessage id="cash-payment-option__label" defaultMessage="Cash" />,
			description: (
				<FormattedMessage
					id="cash-payment-option__description"
					defaultMessage="Please take the cash before completing this booking."
				/>
			),
		},
	] as PaymentOption[];

	// `canPayWithPreviousCards` gates the request rather than being checked inside it. Checked inside,
	// a render where it was still false (roles not loaded yet, say) cached an empty list under this
	// user's key, and the cards never appeared once it turned true.
	const showPreviousCards = canPayWithPreviousCards && stringNotEmpty(userId);
	const { isLoading: isPreviousCardsLoading, data: previousCards } = useQuery({
		...previousCardsQueryOptions(userId ?? ''),
		enabled: showPreviousCards,
		// The saved-card list changes out-of-band: completing a card payment saves a new Card on File
		// to this same customer. With the app's default 30s staleTime, the very next booking's payment
		// step would serve the cached (pre-save) list and hide the card the customer just saved until a
		// hard refresh. staleTime 0 revalidates on every mount so a freshly-saved card shows immediately.
		//
		// The booking wizard is the exception: its PaymentTab fetches this list afresh when it mounts,
		// while extend-reservation and setup-payment run, so anything cached here was fetched for this
		// payment step. Revalidating it again would only repeat that request.
		staleTime: paymentLocation === 'booking-wizard' ? PREFETCHED_PREVIOUS_CARDS_STALE_MS : 0,
	});

	const previousCardsData: PaymentOption[] = showPreviousCards
		? (previousCards ?? []).map(pm => ({
				id: pm.id as PaymentMethodId,
				condition: true,
				dataTestId: 'payment-option-previous-card',
				label: (
					<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<FormattedMessage
							id="previous-card-payment-option__label"
							defaultMessage="Pay with card ending in {cardLast4}"
							values={{
								cardLast4: pm.last4,
							}}
						/>
						<SandboxTooltipBadge tooltip="This will not charge a real card." />
					</span>
				),
				description: (
					<FormattedMessage
						id="previous-card-payment-option__description"
						defaultMessage="Please verify the last 4 digits of the card before proceeding with payment."
					/>
				),
			}))
		: [];

	const payLaterOptions: PaymentOption[] = [
		{
			id: 'invoice-option',
			condition: canInvoice || !!invoiceDisabledForPaidBooking,
			disabled: !!invoiceDisabledForPaidBooking,
			label: <FormattedMessage id="invoice-payment-option__label" defaultMessage="Send invoice" />,
			description: (
				<FormattedMessage
					id="invoice-payment-option__description"
					defaultMessage="A separate invoice will be issued through our accounting system in line with our agreed payment terms."
				/>
			),
			headerNote: invoiceDisabledForPaidBooking ? (
				<FormattedMessage
					id="invoice-payment-option__paid-booking-disabled"
					defaultMessage="A booking that has previously been paid for can't be invoiced."
				/>
			) : undefined,
		},
		{
			id: 'pay-later',
			condition: canPayLater,
			label: <FormattedMessage id="pay-later-payment-option-v2__label" defaultMessage="Pay later" />,
			// Staff+ booking on behalf see the operational detail (split transactions, fee handling);
			// a customer self-booking sees simpler reassurance.
			description: store.isStaff ? (
				<FormattedMessage
					id="pay-later-payment-option-v2__description"
					defaultMessage="Payment will need to be taken at a later time."
				/>
			) : (
				<FormattedMessage
					id="pay-later-payment-option-v2__description__customer"
					defaultMessage="You can complete payment for this booking at a later time."
				/>
			),
			additionalContent: store.isStaff ? (
				<div>
					<strong>Note:</strong>{' '}
					<FormattedMessage
						id="pay-later-payment-option-v2__note__staff"
						defaultMessage="Each booking will be split into its own transaction so it can be paid for separately later. Discounts and service fees are removed once each booking is completed."
					/>
				</div>
			) : (
				<div>
					<strong>Note:</strong>{' '}
					<FormattedMessage
						id="pay-later-payment-option-v2__note__customer"
						defaultMessage="Your booking is held, but payment must be completed before travel."
					/>
				</div>
			),
		},
	];

	const renderPaymentOption = (option: PaymentOption) => {
		const optionDisabled = disabled || !!option.disabled;
		return (
			<div
				key={option.id}
				className={classNames(
					'payment-option',
					{
						active: activePaymentMethod === option.id,
					},
					{
						disabled: optionDisabled,
					},
				)}
				id={option.id}
				data-test-id={option.dataTestId}
			>
				<div
					className="payment-option__header"
					onClick={() => {
						if (!optionDisabled) {
							togglePayment(option.id);
						}
					}}
					onKeyDown={() => {}}
				>
					<div className="radio-button" />
					<div className="payment-option-label">
						{option.label}
						{option.headerNote && (
							<div className="payment-option-label__note">
								<span className="material-symbols-outlined payment-option-label__note-icon">help</span>
								<span>{option.headerNote}</span>
							</div>
						)}
					</div>
				</div>
				<div className="payment-option-content">
					<div className="payment-info-text">
						{option.description}
						{option.additionalContent}
					</div>
				</div>
			</div>
		);
	};

	const renderPaymentGroup = (
		title: { labelId: string; defaultMessage: string; staffLabelId: string; staffDefaultMessage: string },
		options: PaymentOption[],
		previousCardOptions?: PaymentOption[],
		includeCard = false,
	) => {
		const visibleOptions = options.filter(option => option.condition);
		if (
			!visibleOptions.length &&
			(isNullOrUndefined(previousCardOptions) || !previousCardOptions.length) &&
			!includeCard
		) {
			return null;
		}

		return (
			<div className="payment-method-group-section">
				<If condition={hasAnyPaymentOptions}>
					<p className="payment-method-group__header">
						<If condition={!store.isStaff}>
							<FormattedMessage id={title.labelId} defaultMessage={title.defaultMessage} />
						</If>
						<If condition={store.isStaff}>
							<FormattedMessage id={title.staffLabelId} defaultMessage={title.staffDefaultMessage} />
						</If>
					</p>
				</If>
				<div className="payment-method-group">
					{!isGuest && previousCardOptions && previousCardOptions.length > 0 && (
						<>{previousCardOptions?.map(renderPaymentOption)}</>
					)}
					<If condition={includeCard && isNotNullOrUndefined(cardPaymentMethod)}>{cardPaymentMethod}</If>
					{visibleOptions.map(renderPaymentOption)}
				</div>
			</div>
		);
	};

	const payNowGroup = renderPaymentGroup(
		{
			labelId: 'payment-tab__pay-now__label',
			defaultMessage: 'Pay now',
			staffLabelId: 'payment-tab__pay-now__staff__label',
			staffDefaultMessage: 'Take payment now',
		},
		payNowOptions,
		previousCardsData,
		canPayByCard,
	);

	const payLaterGroup =
		canInvoice || canPayLater || !!invoiceDisabledForPaidBooking
			? renderPaymentGroup(
					{
						labelId: 'payment-tab__pay-later__label',
						defaultMessage: 'Pay later',
						staffLabelId: 'payment-tab__pay-later__staff__label',
						staffDefaultMessage: 'Record payment for later',
					},
					payLaterOptions,
					undefined,
				)
			: null;

	return (
		<div className="payment-type-selection__container">
			<If condition={paymentProcessing}>
				<div className="payment-type-selection__loader">
					<LottieWaveDotLoader />
					<div className="loader-container">
						<div className="loading-text dots">Processing payment</div>
						<If condition={paymentMethod === 'terminal-option'}>
							<Button
								display={Display.Text}
								colors={Colors.Error}
								sizes={Sizes.Small}
								onClick={() => {
									clearTerminalPolling?.();
								}}
							>
								{intl.formatMessage({
									id: 'pos__payment-option__cancel',
									defaultMessage: 'Cancel POS Payment',
								})}
							</Button>
						</If>
					</div>
				</div>
			</If>
			<If condition={!paymentProcessing && isPreviousCardsLoading}>
				<Spinner />
			</If>
			<If condition={!isPreviousCardsLoading}>
				<div className={classNames('payment-method__wrap', { hidden: paymentProcessing })}>
					{payLaterSectionFirst ? (
						<>
							{payLaterGroup}
							{payNowGroup}
						</>
					) : (
						<>
							{payNowGroup}
							{payLaterGroup}
						</>
					)}
				</div>
			</If>
		</div>
	);
}

export default observer(PaymentTypeSelection);
